// Phase 2.2: <PeopleMultiSelect> — the single people-picker used by every
// assignment surface (AssignmentEditModal, JobScheduleSection,
// SchedulePhaseDialog). Replaces the legacy single-crew button chips.
//
// Shape: tag-input. Selected people render as crossable chips; a search box
// adds active people by name. (Crew-preset quick-add was removed 2026-06-12.)
// `singleSelect` constrains it to one person (replace-on-pick) and relabels the
// input — used by the Crew-Utilization time-entry form.

import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { getCrewMembers } from '../api/client.js';
import { cn } from '../lib/utils.js';

interface PeopleMultiSelectProps {
  /** Selected crew_member ids. */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /**
   * Single-person mode: picking replaces the current selection (max 1) and the
   * input reads "Add person" / "Change person" instead of the multi label.
   */
  singleSelect?: boolean;
  /**
   * Person ids that are already on this job in another assignment row —
   * shown but not addable (mirrors the legacy sibling-crew guard).
   */
  unavailableIds?: Set<string>;
  unavailableReason?: (id: string) => string | undefined;
}

export function PeopleMultiSelect({
  value,
  onChange,
  disabled = false,
  singleSelect = false,
  unavailableIds,
  unavailableReason,
}: PeopleMultiSelectProps) {
  const [search, setSearch] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: members } = useQuery({
    queryKey: ['crew-members'],
    queryFn: getCrewMembers,
  });

  // Active FIELD CREW only — inactive members can't take new work, and
  // role 'admin' (the office-manager Telegram identity) is not assignable.
  // (crew_members.role — unrelated to app_users.admin.) The server guard
  // enforces the same on writes.
  const activePeople = useMemo(
    () => (members ?? []).filter((m) => m.active && m.role !== 'admin'),
    [members],
  );
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members ?? []) map.set(m.id, m.name);
    return map;
  }, [members]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  // Escape closes the dropdown; handled locally on the input (with
  // stopPropagation) so it doesn't also close a surrounding modal.

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activePeople
      .filter((m) => !selectedSet.has(m.id))
      .filter((m) => (q ? m.name.toLowerCase().includes(q) : true))
      .slice(0, 8);
  }, [activePeople, selectedSet, search]);

  function add(id: string) {
    if (selectedSet.has(id)) return;
    if (unavailableIds?.has(id)) return;
    // singleSelect: picking replaces the current selection (max 1).
    onChange(singleSelect ? [id] : [...value, id]);
    setSearch('');
    // Close the dropdown after each pick; the user re-clicks the field to add
    // another (the input keeps focus, so onClick — not onFocus — reopens it).
    setListOpen(false);
  }

  function remove(id: string) {
    onChange(value.filter((v) => v !== id));
  }

  const placeholder = singleSelect
    ? value.length === 0
      ? 'Add person'
      : 'Change person'
    : 'Add a person…';

  return (
    <div ref={containerRef} className="space-y-1.5">
      {/* Selected chips */}
      <div className="flex flex-wrap gap-1">
        {value.length === 0 && (
          <span className="text-xs text-muted-foreground">No one assigned yet</span>
        )}
        {value.map((id) => (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded-md border border-primary bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
          >
            {nameById.get(id) ?? 'Unknown'}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${nameById.get(id) ?? 'person'}`}
                onClick={() => remove(id)}
                className="rounded-sm hover:bg-primary/20"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>

      {!disabled && (
        <>
          {/* Search-as-you-type */}
          <div className="relative">
            <input
              type="text"
              value={search}
              placeholder={placeholder}
              onChange={(e) => {
                setSearch(e.target.value);
                setListOpen(true);
              }}
              onFocus={() => setListOpen(true)}
              // Re-clicking the already-focused field reopens the list after a
              // pick closed it (onFocus won't fire when focus is retained).
              onClick={() => setListOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && listOpen) {
                  e.stopPropagation();
                  setListOpen(false);
                }
              }}
              onBlur={() => {
                // Delay so a click on a list row registers before close.
                window.setTimeout(() => setListOpen(false), 120);
              }}
              className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm focus:border-primary focus:outline-none"
            />
            {listOpen && matches.length > 0 && (
              <ul
                onMouseDown={(e) => {
                  // Keep the input focused when grabbing the scrollbar / list chrome, so
                  // the blur-timeout doesn't close the dropdown mid-drag. Option buttons
                  // handle their own mousedown; act only when the target IS the list.
                  if (e.target === e.currentTarget) e.preventDefault();
                }}
                className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md border border-border bg-card py-1 shadow-md"
              >
                {matches.map((m) => {
                  const blocked = unavailableIds?.has(m.id);
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        disabled={blocked}
                        title={blocked ? unavailableReason?.(m.id) : undefined}
                        onMouseDown={(e) => {
                          // onMouseDown (not onClick) so it fires before blur.
                          e.preventDefault();
                          if (!blocked) add(m.id);
                        }}
                        className={cn(
                          'flex w-full items-center gap-2 px-2 py-1 text-left text-sm hover:bg-accent/40',
                          blocked && 'cursor-not-allowed opacity-40 line-through hover:bg-transparent',
                        )}
                      >
                        {m.crewColor && (
                          <span
                            className="inline-block h-2 w-2 rounded-full"
                            style={{ backgroundColor: m.crewColor }}
                          />
                        )}
                        {m.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
